import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { type ChatApi, createChatApi } from 'features/chatbot/api/chatApi'
import {
  connectToStream,
  type SSEConnection,
} from 'features/chatbot/api/sseClient'
import type { ChatSession } from 'features/chatbot/contexts/ChatbotSessionContext'
import type { ChatbotContextInfo } from 'features/chatbot/hooks/useChatbotContext'
import type {
  AgentMode,
  DocumentRef,
  InstrumentRef,
} from 'features/chatbot/types/chat'
import {
  ConversationMessage,
  Source,
  ThinkingNote,
} from 'features/chatbot/types/conversation'
import type { NaturalLanguageResponseSegment } from 'features/chatbot/types/response'
import type { ChatStreamEvent } from 'features/chatbot/types/stream'
import { DocumentAgentState } from 'features/knowledgeDocumentViewer'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useMemo, useRef } from 'react'
import { generateChatSessionId } from 'types'

interface UseMessageSenderParams {
  session: ChatSession
  contextInfo: ChatbotContextInfo
  documentAgentState?: DocumentAgentState
  appendMessage: (message: ConversationMessage) => void
  setIsThinking: (isThinking: boolean) => void
  getAttachmentContext: () => string
  getDocumentViewerContext: () => string
  onStreamingThoughtsChange: (thoughts: ThinkingNote[]) => void
  onStreamStarted: (streamId: string, messageId: string) => void
  onStreamSeq: (seq: number) => void
  onStreamComplete: (status: 'complete' | 'error') => void
  onEntryIdAttached: (entryId: string) => void
  /** Stream severed before the agent finished; handler should attempt reconnection. */
  onStreamInterrupted?: () => void
}

interface UseMessageSenderReturn {
  sendChatMessage: (message: string) => void
  sendRCAMessage: (message: string, context?: RCAContextCompat) => void
  cancelGeneration: () => void
  handleStreamEvent: (event: ChatStreamEvent) => void
}

export interface RCAContextCompat {
  siteId?: string
  orgId?: string
  unitId?: string
  assetId?: string
  componentId?: string
  failureDate?: string
}

function documentRefToSource(doc: DocumentRef): Source {
  return {
    id: doc.id,
    text: doc.title || doc.drawingNumber || doc.id,
    data: {
      id: doc.id,
      title: doc.title,
      drawingNumber: doc.drawingNumber,
      documentType: doc.documentType,
      pageNumber: doc.pageNumber,
    },
  }
}

function instrumentRefToSource(inst: InstrumentRef): Source {
  return {
    id: `inst-${inst.id}`,
    text: inst.instrumentName,
    data: {
      instrumentName: inst.instrumentName,
      entryId: inst.id,
      startDatetime: inst.startDatetime,
      endDatetime: inst.endDatetime,
      piTag: inst.piTag,
    },
  }
}

export function useMessageSender({
  session,
  contextInfo,
  documentAgentState,
  appendMessage,
  setIsThinking,
  getAttachmentContext,
  getDocumentViewerContext,
  onStreamingThoughtsChange,
  onStreamStarted,
  onStreamSeq,
  onStreamComplete,
  onEntryIdAttached,
  onStreamInterrupted,
}: UseMessageSenderParams): UseMessageSenderReturn {
  const { authorization } = useGeckoAuth()
  const { user } = useGeckoPermissions()
  const { LLM_SERVICE } = useRuntimeVariables()

  const streamingSources = useRef<Source[]>([])
  const thinkingStartTime = useRef<number | null>(null)
  const collectedThoughts = useRef<ThinkingNote[]>([])
  const currentAssistantMessageId = useRef<string | null>(null)

  const connectionRef = useRef<SSEConnection | { abort: () => void } | null>(
    null
  )

  const pendingEntryIdRef = useRef<string>(
    session.entryId ?? generateChatSessionId().entryId
  )

  const pendingEnsureRef = useRef<Promise<string> | null>(null)

  const api = useMemo<ChatApi>(
    () => createChatApi(LLM_SERVICE, authorization ?? ''),
    [LLM_SERVICE, authorization]
  )

  const attachSourcesToLastThought = useCallback(
    (sources: Source[]) => {
      if (sources.length === 0) return

      const thoughts = collectedThoughts.current
      if (thoughts.length === 0) {
        thoughts.push({ text: '', sources })
      } else {
        const last = thoughts[thoughts.length - 1]
        last.sources = [...(last.sources || []), ...sources]
      }

      streamingSources.current = [...streamingSources.current, ...sources]
      onStreamingThoughtsChange([...thoughts])
    },
    [onStreamingThoughtsChange]
  )

  const handleStreamEvent = useCallback(
    (event: ChatStreamEvent) => {
      onStreamSeq(event.seq)
      if (event.type !== 'stream_started') {
        const withMsgId = event as { messageId?: string }
        if (withMsgId.messageId) {
          currentAssistantMessageId.current = withMsgId.messageId
        }
      }
      switch (event.type) {
        case 'stream_started':
          currentAssistantMessageId.current = event.messageId
          onStreamStarted(event.streamId, event.messageId)
          break

        case 'thinking': {
          collectedThoughts.current.push({
            id: event.thoughtId,
            text: event.text,
          })
          appendMessage({ type: 'thought', text: event.text })
          break
        }

        case 'tool_call': {
          const functionName = event.name
          const text = t`Calling ${functionName}`
          // Carry tool metadata into the thought so specialized
          // renderers (e.g. case proposal cards) can detect the call
          // and render appropriate UI. The default renderer falls
          // back to the "Calling X" text.
          const toolCall = { name: event.name, args: event.args }
          collectedThoughts.current.push({
            id: event.thoughtId,
            text,
            toolCall,
          })
          appendMessage({ type: 'thought', text })
          break
        }

        case 'tool_result': {
          const refs = event.references
          const newSources: Source[] = [
            ...refs.documents.map(documentRefToSource),
            ...refs.instruments.map(instrumentRefToSource),
          ].filter(
            (s) =>
              !streamingSources.current.some((existing) => existing.id === s.id)
          )
          if (newSources.length > 0) {
            attachSourcesToLastThought(newSources)
          }
          // For tools whose result carries a stable client-visible id
          // (currently `propose_case_update` -> proposal_id), copy it
          // back into the matching thought's toolCall.args so live UI
          // (e.g. ProposalCard) can render before reload. Backend
          // mirrors this into the persisted thought via streaming.py.
          if (event.name === 'propose_case_update' && event.content) {
            const proposalId = event.content.proposal_id
            if (typeof proposalId === 'string' && proposalId) {
              const lastThought =
                collectedThoughts.current[collectedThoughts.current.length - 1]
              if (
                lastThought &&
                lastThought.toolCall?.name === 'propose_case_update'
              ) {
                const existingArgs =
                  lastThought.toolCall.args &&
                  typeof lastThought.toolCall.args === 'object'
                    ? (lastThought.toolCall.args as Record<string, unknown>)
                    : {}
                lastThought.toolCall = {
                  ...lastThought.toolCall,
                  args: { ...existingArgs, proposal_id: proposalId },
                }
              }
            }
          }
          break
        }

        case 'references_updated': {
          const newSources: Source[] = [
            ...event.documents.map(documentRefToSource),
            ...event.instruments.map(instrumentRefToSource),
          ].filter(
            (s) =>
              !streamingSources.current.some((existing) => existing.id === s.id)
          )
          if (newSources.length > 0) {
            attachSourcesToLastThought(newSources)
          }
          break
        }

        case 'text':
          break

        case 'done': {
          const thinkingTimeSeconds = thinkingStartTime.current
            ? Math.round((Date.now() - thinkingStartTime.current) / 1000)
            : event.thinkingTimeSeconds

          const documentSources = event.allDocuments.map(documentRefToSource)
          const instrumentSources = event.allInstruments.map(
            instrumentRefToSource
          )
          const allFinalSources = [...documentSources, ...instrumentSources]

          const segments: NaturalLanguageResponseSegment[] = [
            { text: event.content, citations: [] },
          ]

          const instrumentsWithData = event.allInstruments.map((inst) => ({
            instrumentName: inst.instrumentName,
            entryId: inst.id,
            startDatetime: inst.startDatetime,
            endDatetime: inst.endDatetime,
          }))

          appendMessage({
            type: 'aiResponse',
            messageId:
              event.messageId || currentAssistantMessageId.current || undefined,
            naturalLanguageResponseBySegments: segments,
            sources: allFinalSources.length > 0 ? allFinalSources : undefined,
            thinkingTimeSeconds,
            thinkingNotes:
              collectedThoughts.current.length > 0
                ? [...collectedThoughts.current]
                : undefined,
            instrumentsWithData:
              instrumentsWithData.length > 0 ? instrumentsWithData : undefined,
          })

          onStreamingThoughtsChange([])
          setIsThinking(false)
          streamingSources.current = []
          thinkingStartTime.current = null
          collectedThoughts.current = []
          currentAssistantMessageId.current = null
          onStreamComplete('complete')
          break
        }

        case 'error':
          appendMessage({ type: 'error', text: event.error })
          setIsThinking(false)
          streamingSources.current = []
          thinkingStartTime.current = null
          collectedThoughts.current = []
          currentAssistantMessageId.current = null
          onStreamingThoughtsChange([])
          onStreamComplete('error')
          break

        case 'image':
        case 'progress':
          break
      }
    },
    [
      appendMessage,
      setIsThinking,
      onStreamingThoughtsChange,
      attachSourcesToLastThought,
      onStreamStarted,
      onStreamSeq,
      onStreamComplete,
    ]
  )

  const ensureSession = useCallback(
    (
      mode: AgentMode,
      context: Record<string, string>,
      options?: {
        failureDate?: string
        linkedDocumentIds?: string[]
      }
    ): Promise<string> => {
      if (session.entryId) return Promise.resolve(session.entryId)
      if (pendingEnsureRef.current) return pendingEnsureRef.current

      const clientEntryId = pendingEntryIdRef.current

      // If a caller pinned a mode/context at session-create time
      // (e.g. the case page's "Ask about this case" button), prefer
      // those over the defaults the message sender wants to use.
      // This guarantees the backend skips routing for pinned modes.
      const effectiveMode: AgentMode = session.pinnedMode ?? mode
      const effectiveContext: Record<string, string> = session.pinnedContext
        ? { ...context, ...session.pinnedContext }
        : context

      const promise = (async () => {
        try {
          const response = await api.createSession({
            mode: effectiveMode,
            context: effectiveContext,
            entry_id: clientEntryId,
            failure_date: options?.failureDate,
            linked_document_ids: options?.linkedDocumentIds,
          })
          onEntryIdAttached(response.session_id)
          return response.session_id
        } finally {
          pendingEnsureRef.current = null
        }
      })()

      pendingEnsureRef.current = promise
      return promise
    },
    [
      api,
      session.entryId,
      session.pinnedMode,
      session.pinnedContext,
      onEntryIdAttached,
    ]
  )

  const cancelGeneration = useCallback(() => {
    connectionRef.current?.abort()
    connectionRef.current = null
    setIsThinking(false)
    streamingSources.current = []
    onStreamingThoughtsChange([])
    thinkingStartTime.current = null
    collectedThoughts.current = []
    currentAssistantMessageId.current = null
  }, [setIsThinking, onStreamingThoughtsChange])

  const sendChatMessage = useCallback(
    (message: string) => {
      streamingSources.current = []
      collectedThoughts.current = []

      const attachmentContextString = getAttachmentContext()
      const documentViewerContextString = getDocumentViewerContext()

      // eslint-disable-next-line lingui/no-unlocalized-strings -- Model instruction template, not UI copy.
      const enrichedContent = `The user has asked you a question: ${message}. Respond to the user's question.

        Here is some automatically extracted page context:
        ${JSON.stringify(contextInfo, null, 2)}${attachmentContextString}${documentViewerContextString}
        `

      appendMessage({ type: 'userMessage', text: message })
      setIsThinking(true)
      thinkingStartTime.current = Date.now()

      const abortController = new AbortController()
      connectionRef.current = { abort: () => abortController.abort() }

      fetch(`${LLM_SERVICE}/api/v1/chat/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authorization ?? '',
        },
        body: JSON.stringify({
          user_id: 'chatbot',
          new_message: { role: 'user', parts: [{ text: enrichedContent }] },
        }),
        signal: abortController.signal,
      })
        .then(async (res) => {
          if (!res.ok || !res.body) {
            throw new Error(`V1 chat request failed: ${res.status}`)
          }
          const reader = res.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''
          let finalContent = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''

            for (const line of lines) {
              if (!line.startsWith('data: ')) continue
              try {
                const event = JSON.parse(line.slice(6)) as Record<
                  string,
                  unknown
                >
                switch (event.type) {
                  case 'thought':
                    collectedThoughts.current.push({
                      text: event.content as string,
                    })
                    appendMessage({
                      type: 'thought',
                      text: event.content as string,
                    })
                    break
                  case 'tool_call': {
                    const functionName = event.function_name as string
                    const toolCallText = t`Calling ${functionName}`
                    collectedThoughts.current.push({ text: toolCallText })
                    appendMessage({ type: 'thought', text: toolCallText })
                    break
                  }
                  case 'response':
                    finalContent +=
                      typeof event.content === 'string'
                        ? event.content
                        : JSON.stringify(event.content)
                    break
                  case 'error':
                    appendMessage({
                      type: 'error',
                      text:
                        (event.error as string) ||
                        (event.content as string) ||
                        t`Unknown error`,
                    })
                    setIsThinking(false)
                    return
                }
              } catch {
                continue
              }
            }
          }

          const thinkingTimeSeconds = thinkingStartTime.current
            ? Math.round((Date.now() - thinkingStartTime.current) / 1000)
            : 0

          const segments: NaturalLanguageResponseSegment[] = [
            { text: finalContent, citations: [] },
          ]

          appendMessage({
            type: 'aiResponse',
            naturalLanguageResponseBySegments: segments,
            thinkingTimeSeconds,
            thinkingNotes:
              collectedThoughts.current.length > 0
                ? [...collectedThoughts.current]
                : undefined,
          })

          onStreamingThoughtsChange([])
          setIsThinking(false)
          streamingSources.current = []
          thinkingStartTime.current = null
          collectedThoughts.current = []
          connectionRef.current = null
        })
        .catch((err: Error) => {
          if (err.name === 'AbortError') return
          appendMessage({ type: 'error', text: err.message })
          setIsThinking(false)
          connectionRef.current = null
        })
    },
    [
      LLM_SERVICE,
      authorization,
      contextInfo,
      getAttachmentContext,
      getDocumentViewerContext,
      appendMessage,
      setIsThinking,
      onStreamingThoughtsChange,
    ]
  )

  const sendRCAMessage = useCallback(
    (message: string, context?: RCAContextCompat) => {
      streamingSources.current = []
      thinkingStartTime.current = Date.now()
      collectedThoughts.current = []

      const effectiveSiteId =
        context?.siteId ||
        documentAgentState?.siteId ||
        contextInfo.siteId ||
        ''
      const effectiveOrgId =
        context?.orgId ||
        documentAgentState?.orgId ||
        (contextInfo.organizationIds?.[0] as string | undefined) ||
        ''

      const documentViewerContextString = getDocumentViewerContext()
      const enrichedQuery = documentViewerContextString
        ? `${message}\n\nContext:${documentViewerContextString}`
        : message

      appendMessage({ type: 'userMessage', text: message })
      setIsThinking(true)

      const sessionContext: Record<string, string> = {
        org_id: effectiveOrgId,
        site_id: effectiveSiteId,
        user_id: user?.id ?? '',
      }
      if (context?.unitId) sessionContext.unit_id = context.unitId
      if (context?.assetId) sessionContext.asset_id = context.assetId
      if (context?.componentId)
        sessionContext.component_id = context.componentId

      ensureSession('auto', sessionContext, {
        failureDate: context?.failureDate,
      })
        .then((sid) => {
          connectionRef.current = connectToStream(
            api,
            sid,
            { content: enrichedQuery, include_tool_logs: true },
            {
              onEvent: handleStreamEvent,
              onError: (error) => {
                appendMessage({ type: 'error', text: error })
                setIsThinking(false)
                onStreamComplete('error')
              },
              onComplete: () => {
                connectionRef.current = null
              },
              onDisconnect: () => {
                connectionRef.current = null
                if (onStreamInterrupted) {
                  onStreamInterrupted()
                } else {
                  appendMessage({
                    type: 'error',
                    text: t`Connection to the assistant was interrupted.`,
                  })
                  setIsThinking(false)
                  onStreamComplete('error')
                }
              },
            }
          )
        })
        .catch((err: Error) => {
          appendMessage({ type: 'error', text: err.message })
          setIsThinking(false)
        })
    },
    [
      contextInfo,
      documentAgentState,
      getDocumentViewerContext,
      appendMessage,
      setIsThinking,
      ensureSession,
      api,
      handleStreamEvent,
      user?.id,
      onStreamComplete,
      onStreamInterrupted,
    ]
  )

  return {
    sendChatMessage,
    sendRCAMessage,
    cancelGeneration,
    handleStreamEvent,
  }
}
